/**
 * O mapa de uma automação: o começo e os blocos ligados, como o serviço guarda.
 *
 * Com `versao`, é uma versão antiga, do histórico, e só se lê. Sem ela, é a de
 * agora. A automação de modelo no formato antigo (`grafo: null`) não tem mapa:
 * a que tem a configuração do modelo mostra essa configuração (que a casca
 * entrega, da área das automações), e as outras dizem que não têm mapa em vez
 * de desenhar um começo sozinho.
 */

import { useId, useMemo, useState, type ReactNode } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Background, ReactFlow, type Node } from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import type { AutomacaoDaApi, BlocosDoEditor } from '@cliqcrm/contracts';
import { chaves } from '@/shared/api/chaves';
import {
  useApi,
  useAutomacao,
  useBlocosDoEditor,
  useHistoricoDaAutomacao,
} from '@/shared/api/consultas';
import { recadoDeErro } from '@/shared/api/recado-de-erro';
import { Botao, CabecalhoDeTela, Dialogo, Icone, Selo, type TomSelo } from '@/shared/ui';
import { Estado, Vazio } from '@/shared/ui/estados/estados';
import { Faixa } from '@/shared/ui/estados/faixa';
import { montaOTipoDaAutomacao, type QuemNaTela } from '@/entities/acesso/papel';
import { useMe } from '@/shared/sessao/contexto';
import { desenharMapa } from '../lib/desenho';
import { configuracaoDe, versaoEditada } from '../lib/edicao';
import {
  FerramentasDoMapa,
  LegendaDoMapa,
  TIPOS_DE_BLOCO,
  TIPOS_DE_LIGACAO,
  ligacaoParaOMapa,
  type DadosDoBloco,
} from './bloco-do-mapa';
import { EdicaoDoMapa } from './edicao-do-mapa';
import './editor-do-mapa.css';
import { comporMenus } from '../lib/menu';

const QUANDO = new Intl.DateTimeFormat('pt-BR', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

/**
 * Como cada situação aparece, com as palavras e as cores da lista das
 * automações (que mora na área, e o editor não importa).
 */
const SITUACAO: Record<AutomacaoDaApi['situacao'], { rotulo: string; tom: TomSelo }> = {
  rascunho: { rotulo: 'Rascunho', tom: 'rascunho' },
  em_homologacao: { rotulo: 'Em teste', tom: 'atencao' },
  ativa: { rotulo: 'Ativa', tom: 'ok' },
  pausada: { rotulo: 'Pausada', tom: 'neutro' },
  arquivada: { rotulo: 'Arquivada', tom: 'neutro' },
};

/** Quem monta aquele tipo de automação: a mesma régua do backend (e da lista). */
function monta(quem: QuemNaTela, a: Pick<AutomacaoDaApi, 'usa_sql'>): boolean {
  return montaOTipoDaAutomacao(quem, a.usa_sql);
}

export function EditorDoMapa({
  id,
  versao,
  recemCriado = false,
  aoVoltar,
  aoVerAtual,
  historico: desenharHistorico,
  modelo: desenharModelo,
}: {
  id: string;
  versao: number | null;
  /** Acabou de ser criada do zero: o mapa abre com o recado do rascunho. */
  recemCriado?: boolean;
  aoVoltar: () => void;
  aoVerAtual: () => void;
  /** O histórico de versões, que mora na área das automações e a casca entrega. */
  historico?: (automacao: AutomacaoDaApi, nome: string, aoFechar: () => void) => ReactNode;
  /**
   * A configuração da automação de modelo pronto, sem mapa (a rotina agendada,
   * o lembrete), para ler e editar. Mora na área das automações; a casca entrega.
   */
  modelo?: (automacao: AutomacaoDaApi) => ReactNode;
}): ReactNode {
  const automacao = useAutomacao(id);
  const blocos = useBlocosDoEditor();
  const historico = useHistoricoDaAutomacao(id);
  const quem = useMe();

  // A versão antiga sai da foto do histórico; a de agora, do detalhe.
  const daVersao =
    versao === null
      ? undefined
      : historico.data?.itens.find((v) => v.versao === versao && v.foto !== null);
  const foto = daVersao?.foto ?? null;
  const [restaurando, definirRestaurando] = useState(false);
  const [vendoHistorico, definirVendoHistorico] = useState(false);
  // Quantas versões há: uma versão tem várias linhas (editada, depois ativada).
  const versoes = new Set(historico.data?.itens.map((v) => v.versao) ?? []).size;

  return (
    <div className="editor-do-mapa">
      <Estado consulta={automacao} forma="ficha">
        {(a) => {
          const nome = a.nome?.trim() || 'Automação sem nome';
          // Edita quem monta aquele tipo (a mesma régua do backend), na versão de
          // agora, e não a arquivada nem a interna de um modelo.
          const inicial = configuracaoDe(a);
          const editando =
            versao === null &&
            quem !== null &&
            a.principal &&
            a.situacao !== 'arquivada' &&
            monta(quem, a) &&
            inicial !== null &&
            // O gatilho interno (de modelo) não se edita: o serviço só aceita evento e agenda.
            a.gatilho.tipo !== 'interno' &&
            blocos.data !== undefined;
          const selo = (
            <Selo tom={SITUACAO[a.situacao].tom} vivo={a.situacao === 'ativa'}>
              {SITUACAO[a.situacao].rotulo}
            </Selo>
          );
          const botaoDoHistorico = desenharHistorico ? (
            <Botao variante="secundario" onClick={() => definirVendoHistorico(true)}>
              <Icone nome="relogio" />
              {versoes > 0 ? `Histórico (${versoes})` : 'Histórico'}
            </Botao>
          ) : undefined;
          return (
            <>
              {/* Editando, o mapa ocupa a tela: o cabeçalho é a barra da edição. */}
              {!editando && (
                <CabecalhoDeTela
                  titulo={nome}
                  aoLadoDoTitulo={selo}
                  {...(botaoDoHistorico ? { acoes: botaoDoHistorico } : {})}
                  // Editando, o voltar é o da barra: ele pergunta antes de perder o
                  // que não foi salvo.
                  {...(editando
                    ? {}
                    : {
                        voltar: (
                          <Botao
                            variante="fantasma"
                            icone
                            rotulo="Voltar para Automações"
                            onClick={aoVoltar}
                          >
                            <Icone nome="voltar" />
                          </Botao>
                        ),
                      })}
                  apoio={
                    versao !== null
                      ? `Versão ${versao}, do histórico. Só para ler.`
                      : a.pendente
                        ? `Versão ${a.versao} no ar; a ${a.pendente.versao}, em teste, é a que se edita aqui.`
                        : `Versão ${a.versao}`
                  }
                />
              )}
              {versao !== null && (
                <Faixa
                  tom="defasado"
                  acao={{ rotulo: 'Ver a versão de agora', aoClicar: aoVerAtual }}
                >
                  Esta é a versão {versao}
                  {daVersao ? `, de ${QUANDO.format(new Date(daVersao.em))}` : ''}. A de agora é a{' '}
                  {a.versao}.
                </Faixa>
              )}
              {versao !== null &&
                foto &&
                versao !== versaoEditada(a) &&
                quem !== null &&
                a.situacao !== 'arquivada' &&
                monta(quem, a) &&
                monta(quem, foto) && (
                  <div className="editor-do-mapa__restaurar">
                    <p className="editor-do-mapa__painel-ajuda">
                      Restaurar cria uma versão nova com este mapa. Nada some do histórico.
                    </p>
                    <Botao variante="secundario" miudo onClick={() => definirRestaurando(true)}>
                      <Icone nome="repetir" />
                      Restaurar esta versão
                    </Botao>
                  </div>
                )}
              {vendoHistorico && desenharHistorico?.(a, nome, () => definirVendoHistorico(false))}
              {restaurando && versao !== null && (
                <RestaurarVersao
                  automacao={a}
                  versao={versao}
                  aoFechar={() => definirRestaurando(false)}
                  aoRestaurado={aoVerAtual}
                />
              )}
              {versao !== null && historico.isPending ? null : versao !== null && !foto ? (
                <Vazio
                  titulo="Esta versão não está no histórico"
                  porque="Ela pode ser de antes do histórico guardar o mapa. A versão de agora continua aberta pelo botão acima."
                />
              ) : editando && inicial ? (
                <EdicaoDoMapa
                  automacao={a}
                  inicial={inicial}
                  versaoInicial={versaoEditada(a)}
                  recemCriado={recemCriado}
                  blocos={blocos.data}
                  quem={quem}
                  aoSair={aoVoltar}
                  cabecalho={{
                    titulo: nome,
                    selo,
                    ...(a.pendente
                      ? {
                          apoio: `Versão ${a.versao} no ar; a ${a.pendente.versao}, em teste, é a que se edita aqui.`,
                        }
                      : {}),
                    ...(botaoDoHistorico ? { historico: botaoDoHistorico } : {}),
                  }}
                />
              ) : versao === null &&
                !a.grafo &&
                (a.pendente ?? a).configuracao_do_modelo &&
                desenharModelo ? (
                desenharModelo(a)
              ) : (
                <Mapa automacao={foto ?? a} blocos={blocos.data} />
              )}
            </>
          );
        }}
      </Estado>
    </div>
  );
}

function Mapa({
  automacao,
  blocos,
}: {
  automacao: Pick<AutomacaoDaApi, 'grafo' | 'condicao' | 'gatilho'> &
    Partial<Pick<AutomacaoDaApi, 'outros_comecos'>>;
  blocos: BlocosDoEditor | undefined;
}): ReactNode {
  const { nodes, edges } = useMemo(() => {
    if (!automacao.grafo) return { nodes: [], edges: [] };
    const desenho = desenharMapa(
      comporMenus({
        gatilho: automacao.gatilho,
        condicao: automacao.condicao,
        outros_comecos: [...(automacao.outros_comecos ?? [])],
        grafo: automacao.grafo,
      }),
      blocos,
    );
    const nodes: Node<DadosDoBloco>[] = desenho.blocos.map(({ id, posicao, ...b }) => ({
      id,
      type: 'bloco',
      position: posicao,
      ariaLabel: `${b.titulo}: ${b.resumo}`,
      data: { ...b, faltam: 0, novo: false },
    }));
    return { nodes, edges: desenho.ligacoes.map((l) => ligacaoParaOMapa(l)) };
  }, [automacao, blocos]);

  if (!automacao.grafo) {
    return (
      <Vazio
        titulo="Esta automação não tem mapa"
        porque="Ela vem de um modelo pronto no formato de passos, que não abre no mapa. Ela continua funcionando e se liga e pausa pela lista. Para montar uma automação no mapa, use “Começar do zero”, em Modelos prontos."
      />
    );
  }

  return (
    <section className="editor-do-mapa__plano" aria-label="Mapa da automação">
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={TIPOS_DE_BLOCO}
        edgeTypes={TIPOS_DE_LIGACAO}
        nodesDraggable={false}
        nodesConnectable={false}
        edgesFocusable={false}
        fitView
        minZoom={0.35}
        maxZoom={1.6}
      >
        <Background gap={16} />
        <FerramentasDoMapa />
        <LegendaDoMapa />
      </ReactFlow>
    </section>
  );
}

/**
 * Restaurar uma versão do histórico: o serviço cria uma versão nova com o
 * conteúdo dela. Numa automação no ar, a nova fica em teste, e a do ar segue.
 */
function RestaurarVersao({
  automacao: a,
  versao,
  aoFechar,
  aoRestaurado,
}: {
  automacao: AutomacaoDaApi;
  versao: number;
  aoFechar: () => void;
  aoRestaurado: () => void;
}): ReactNode {
  const api = useApi();
  const cache = useQueryClient();
  const descricao = useId();
  const restaurar = useMutation({
    mutationFn: () => api!.restaurarAutomacao({ caminho: { id: a.id }, corpo: { versao } }),
    onSuccess: () => {
      void cache.invalidateQueries({ queryKey: chaves.automacoes() });
      aoRestaurado();
    },
  });
  return (
    <Dialogo
      titulo={`Restaurar a v${versao}?`}
      largura="estreita"
      papel="alertdialog"
      descricaoId={descricao}
      aoFechar={() => {
        if (!restaurar.isPending) aoFechar();
      }}
      botoes={
        <>
          <Botao
            variante="fantasma"
            onClick={aoFechar}
            desabilitado={restaurar.isPending}
            motivo="Restaurando…"
          >
            Cancelar
          </Botao>
          <Botao
            variante="primario"
            onClick={() => restaurar.mutate()}
            desabilitado={restaurar.isPending || api === null}
            motivo={restaurar.isPending ? 'Restaurando…' : 'Sem sessão: entre de novo.'}
          >
            {restaurar.isPending ? 'Restaurando…' : `Criar a partir da v${versao}`}
          </Botao>
        </>
      }
    >
      <p id={descricao}>
        {a.situacao === 'ativa'
          ? `Vou criar uma versão nova com o conteúdo da v${versao}. A que está no ar continua rodando; a nova fica em teste até alguém ativar.`
          : `Vou criar uma versão nova com o conteúdo da v${versao}, como rascunho. As outras versões continuam no histórico, do jeito que estão.`}
      </p>
      {restaurar.isError && (
        <Faixa tom="bloqueado">
          {recadoDeErro(restaurar.error, {
            generica: 'Não foi possível restaurar agora. Nada mudou; tente de novo.',
            porCodigo: {
              ENGINE_INDISPONIVEL:
                'O serviço das automações não respondeu. Nada mudou; tente de novo daqui a pouco.',
              REQUISICAO_INVALIDA:
                'Esta versão não vale mais como está: ela usa algo que mudou desde então.',
              NAO_ENCONTRADO:
                'Esta versão usa algo que não existe mais, como uma pessoa ou uma lista.',
              SEM_PERMISSAO: 'O seu papel não pode restaurar esta versão.',
            },
          })}
        </Faixa>
      )}
    </Dialogo>
  );
}
